import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Clock, Filter, X, Crown } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PatreonPostCard from '../components/PatreonPostCard';
import { useEpisodes, getAllTags } from '../hooks/useEpisodes';
import { usePatreonPosts } from '../hooks/usePatreonPosts';

// Episode Card Component - Larger artwork style
const EpisodeRow = ({ episode, index }) => {
  const { id, title, description, date, tags, episodeNumber, duration, thumbnail } = episode;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="mb-6"
    >
      <Link
        to={`/episode/${id}`}
        className="group block"
      >
        <div className="episode-row hover:bg-[var(--bg-tertiary)]/30 p-4 -mx-4 rounded-2xl transition-all duration-200">
          {/* Stack vertically on mobile, horizontal on desktop */}
          <div className="flex flex-col md:flex-row gap-4 md:gap-5">
            {/* Episode Thumbnail - Full width on mobile, fixed on desktop */}
            <div className="flex-shrink-0">
              <div className="relative w-full md:w-44 aspect-square md:h-44 rounded-xl overflow-hidden bg-[var(--bg-tertiary)]">
                {thumbnail ? (
                  <img
                    src={thumbnail}
                    alt={title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Play size={32} className="text-[var(--text-muted)]" />
                  </div>
                )}
                {/* Play overlay on hover */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center">
                    <Play size={24} fill="white" className="text-white ml-0.5" />
                  </div>
                </div>
                {/* Episode number badge */}
                <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-mono px-2 py-1 rounded">
                  EP {episodeNumber || '--'}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Title */}
              <h3 className="font-display text-lg md:text-xl text-[var(--text-primary)] group-hover:text-accent transition-colors line-clamp-2 mb-2">
                {title}
              </h3>

              {/* Description - 2 lines max */}
              <p className="text-[var(--text-secondary)] text-sm line-clamp-2 mb-3">
                {description}
              </p>

              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[var(--text-muted)] text-sm">
                <div className="flex items-center gap-1.5">
                  <Clock size={14} />
                  <span>{duration || date}</span>
                </div>
                {tags && tags.length > 0 && (
                  <>
                    <span className="hidden sm:inline">•</span>
                    <div className="flex flex-wrap gap-2">
                      {tags.slice(0, 2).map(tag => (
                        <span key={tag} className="text-accent/80">
                          {tag}
                        </span>
                      ))}
                      {tags.length > 2 && (
                        <span className="text-[var(--text-muted)]">
                          +{tags.length - 2}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// Filter Bar Component
const FilterBar = ({ tags, activeTag, onTagClick }) => {
  return (
    <div className="flex flex-wrap gap-2 mb-8">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => onTagClick(null)}
        className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
          activeTag === null
            ? 'bg-accent text-dark-primary glow-tag-active'
            : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)]'
        }`}
      >
        All
      </motion.button>
      {tags.map(tag => (
        <motion.button
          key={tag}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onTagClick(tag)}
          className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
            activeTag === tag
              ? 'bg-accent text-dark-primary glow-tag-active'
              : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)]'
          }`}
        >
          {tag}
        </motion.button>
      ))}
    </div>
  );
};

const EpisodesPage = () => {
  const { episodes, loading, error } = useEpisodes();
  const { posts: patreonPosts, campaign, loading: patreonLoading } = usePatreonPosts();
  const [activeTag, setActiveTag] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Get all unique tags
  const allTags = useMemo(() => {
    return getAllTags(episodes);
  }, [episodes]);

  // Filter episodes by active tag
  const filteredEpisodes = useMemo(() => {
    if (!activeTag) return episodes;
    return episodes.filter(ep =>
      ep.tags && ep.tags.some(tag =>
        tag.toLowerCase().includes(activeTag.toLowerCase())
      )
    );
  }, [episodes, activeTag]);

  const handleTagClick = (tag) => {
    setActiveTag(tag);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const clearFilter = () => {
    setActiveTag(null);
  };

  return (
    <>
      <Helmet>
        <title>All Episodes | News From The Pew</title>
        <meta name="description" content="Browse the complete archive of News From The Pew podcast episodes. Fresh perspectives on faith, culture, and current events from a conservative Christian worldview." />
        <link rel="canonical" href="https://newsfromthepew.com/episodes" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://newsfromthepew.com/episodes" />
        <meta property="og:title" content="All Episodes | News From The Pew" />
        <meta property="og:description" content="Browse the complete archive of News From The Pew podcast episodes covering faith, culture, and current events." />
        <meta property="og:image" content="https://newsfromthepew.com/assets/images/default_branding_image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="All Episodes | News From The Pew" />
        <meta name="twitter:description" content="Browse the complete archive of News From The Pew podcast episodes covering faith, culture, and current events." />
        <meta name="twitter:image" content="https://newsfromthepew.com/assets/images/default_branding_image.png" />
      </Helmet>
      <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
        <Navigation />

        <main>
        {/* Header Section */}
        <section className="relative py-12 overflow-hidden">
          {/* Ambient glows */}
          <div className="ambient-orb-primary -top-20 -left-20 w-72 h-72" />
          <div className="ambient-orb-secondary -bottom-20 -right-20 w-64 h-64" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <div className="flex items-center gap-4 mb-4">
                <h1 className="font-display text-display-sm md:text-display-md text-[var(--text-primary)]">
                  Episodes
                </h1>
                <div className="flex-1 h-px bg-gradient-to-r from-accent/50 to-transparent" />
              </div>

              {/* Quick Navigation Links */}
              <div className="flex flex-wrap gap-3 mb-6">
                <a
                  href="#free-episodes"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-full transition-all text-sm font-medium"
                >
                  <Play size={14} />
                  Free Episodes
                </a>
                <a
                  href="#member-content"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 hover:bg-accent/20 text-accent rounded-full transition-all text-sm font-medium"
                >
                  <Crown size={14} />
                  Member Content
                </a>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-[var(--text-secondary)] text-lg">
                  Browse {episodes.length} free episodes and exclusive member content
                </p>

                {/* Filter Toggle Button */}
                {allTags.length > 0 && (
                  <div className="flex items-center gap-2">
                    {activeTag && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        onClick={clearFilter}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-accent/10 text-accent rounded-full hover:bg-accent/20 transition-colors"
                      >
                        <span>{activeTag}</span>
                        <X size={14} />
                      </motion.button>
                    )}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={toggleFilters}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-200 ${
                        showFilters
                          ? 'bg-accent text-dark-primary'
                          : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)]'
                      }`}
                    >
                      <Filter size={16} />
                      <span className="text-sm font-medium">Filter</span>
                    </motion.button>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Filter Bar - Collapsible */}
            <AnimatePresence>
              {showFilters && allTags.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pb-4">
                    <p className="text-[var(--text-muted)] text-sm uppercase tracking-wider mb-3">
                      Filter by Topic
                    </p>
                    <FilterBar
                      tags={allTags}
                      activeTag={activeTag}
                      onTagClick={handleTagClick}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* Episode List Section */}
        <section id="free-episodes" className="py-8 bg-[var(--bg-secondary)] scroll-mt-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="flex items-center gap-4 p-4">
                    <div className="hidden md:block w-16 h-4 bg-[var(--bg-tertiary)] rounded" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 bg-[var(--bg-tertiary)] rounded w-3/4" />
                      <div className="h-4 bg-[var(--bg-tertiary)] rounded w-1/2" />
                    </div>
                    <div className="w-10 h-10 bg-[var(--bg-tertiary)] rounded-full" />
                  </div>
                  <div className="h-px bg-[var(--bg-tertiary)]" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-accent mb-4">Error loading episodes</p>
              <p className="text-[var(--text-muted)] text-sm">Please try again later.</p>
            </div>
          ) : filteredEpisodes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[var(--text-secondary)] mb-2">No episodes found</p>
              {activeTag && (
                <button
                  onClick={() => setActiveTag(null)}
                  className="text-accent hover:text-accent-hover transition-colors text-sm"
                >
                  Clear filter
                </button>
              )}
            </div>
          ) : (
            <div>
              {filteredEpisodes.map((episode, index) => (
                <EpisodeRow
                  key={episode.id}
                  episode={episode}
                  index={index}
                />
              ))}
            </div>
          )}
            </motion.div>

            {/* Results count */}
            {!loading && !error && filteredEpisodes.length > 0 && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-[var(--text-muted)] text-sm mt-4 text-center"
              >
                Showing {filteredEpisodes.length} of {episodes.length} episodes
                {activeTag && (
                  <>
                    {' '}filtered by <span className="text-accent">{activeTag}</span>
                  </>
                )}
              </motion.p>
            )}
          </div>
        </section>

        {/* Member Content Section - Patreon */}
        <section id="member-content" className="py-12 scroll-mt-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Section Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-accent/10">
                    <Crown size={20} className="text-accent" />
                  </div>
                  <h2 className="font-display text-display-sm text-[var(--text-primary)]">
                    Member Content
                  </h2>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-accent/50 to-transparent" />
              </div>

              <p className="text-[var(--text-secondary)] mb-6">
                Exclusive content for our Patreon supporters. Join The Upper Room for unposted sermons, unreleased worship demos, and ad-free episodes.
              </p>

              {/* Patreon Posts */}
              {patreonLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse p-5 rounded-xl bg-[var(--bg-tertiary)]/50">
                      <div className="h-5 bg-[var(--bg-tertiary)] rounded w-3/4 mb-3" />
                      <div className="h-4 bg-[var(--bg-tertiary)] rounded w-full mb-2" />
                      <div className="h-4 bg-[var(--bg-tertiary)] rounded w-2/3" />
                    </div>
                  ))}
                </div>
              ) : patreonPosts.length === 0 ? (
                <div className="text-center py-8 px-4 rounded-xl bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)]">
                  <p className="text-[var(--text-muted)]">No Patreon posts available yet.</p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {patreonPosts.map((post, index) => (
                    <PatreonPostCard
                      key={post.id}
                      post={post}
                      index={index}
                    />
                  ))}
                </div>
              )}

              {/* Join Patreon CTA */}
              {campaign && (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="mt-8 text-center"
                >
                  <a
                    href="https://patreon.com/JDavidWyatt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-glow transition-all"
                  >
                    <i className="fab fa-patreon" />
                    Join {campaign.patronCount || ''} Patrons in The Upper Room
                  </a>
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  </>
  );
};

export default EpisodesPage;
